import type { LayerSpecification, StyleSpecification } from 'maplibre-gl';

export type MapStyleKind = 'satellite' | 'dark';

const PROTOMAPS_ATTRIBUTION =
  '<a href="https://protomaps.com" target="_blank" rel="noopener">Protomaps</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

// DECISION: satellite imagery comes from Esri's public World Imagery tile service, the same free,
// attribution-only source the reference game uses. No key, no cost; the vector borders and
// labels on top still come from our own Protomaps tiles, so a service outage only loses the photo.
const ESRI_TILES =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const ESRI_ATTRIBUTION =
  'Tiles © <a href="https://www.esri.com" target="_blank" rel="noopener">Esri</a> — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community';

/**
 * `tilesUrl` is either a PMTiles archive (needs HTTP range support: local dev, R2) or a
 * `{z}/{x}/{y}.pbf` template for the unpacked fallback served as static assets.
 */
function vectorSource(
  tilesUrl: string,
  tilesMaxZoom: number,
): StyleSpecification['sources'][string] {
  return tilesUrl.includes('{z}')
    ? {
        type: 'vector',
        tiles: [tilesUrl],
        minzoom: 0,
        maxzoom: tilesMaxZoom,
        attribution: PROTOMAPS_ATTRIBUTION,
      }
    : { type: 'vector', url: `pmtiles://${tilesUrl}`, attribution: PROTOMAPS_ATTRIBUTION };
}

/**
 * Borders shared by both looks; `onPhoto` tunes contrast for imagery. Deliberately no place names
 * at any zoom: the game is about knowing where things are, so the map never labels it.
 */
function borderLayers(onPhoto: boolean): LayerSpecification[] {
  const border = onPhoto ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.5)';
  const region = onPhoto ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.14)';
  return [
    {
      id: 'boundaries-region',
      type: 'line',
      source: 'basemap',
      'source-layer': 'boundaries',
      minzoom: 3.2,
      filter: ['==', ['get', 'kind'], 'region'],
      paint: {
        'line-color': region,
        'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.4, 7, 1],
        'line-dasharray': [3, 2],
      },
    },
    ...(onPhoto
      ? [
          {
            id: 'boundaries-country-casing',
            type: 'line',
            source: 'basemap',
            'source-layer': 'boundaries',
            filter: ['==', ['get', 'kind'], 'country'],
            paint: {
              'line-color': 'rgba(0,0,0,0.55)',
              'line-width': ['interpolate', ['linear'], ['zoom'], 1, 1.8, 4, 3, 8, 4.5],
            },
          } satisfies LayerSpecification,
        ]
      : []),
    {
      id: 'boundaries-country',
      type: 'line',
      source: 'basemap',
      'source-layer': 'boundaries',
      filter: ['all', ['==', ['get', 'kind'], 'country'], ['!=', ['get', 'disputed'], true]],
      paint: {
        'line-color': border,
        'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.6, 4, 1.2, 8, 2],
      },
    },
    {
      id: 'boundaries-country-disputed',
      type: 'line',
      source: 'basemap',
      'source-layer': 'boundaries',
      filter: ['all', ['==', ['get', 'kind'], 'country'], ['==', ['get', 'disputed'], true]],
      paint: {
        'line-color': border,
        'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.6, 4, 1.2, 8, 2],
        'line-dasharray': [2, 2],
      },
    },
  ];
}

/**
 * Two looks over the same Protomaps v4 tiles (PRD §6.2): a dark vector map, or satellite imagery
 * with borders on top. No labels of any kind: no countries, cities, venues or POIs.
 */
export function buildStyle(
  kind: MapStyleKind,
  tilesUrl: string,
  glyphsUrl: string,
  tilesMaxZoom = 6,
): StyleSpecification {
  const basemap = vectorSource(tilesUrl, tilesMaxZoom);
  if (kind === 'satellite') {
    return {
      version: 8,
      projection: { type: 'globe' },
      glyphs: glyphsUrl,
      sources: {
        imagery: {
          type: 'raster',
          tiles: [ESRI_TILES],
          tileSize: 256,
          maxzoom: 17,
          attribution: ESRI_ATTRIBUTION,
        },
        basemap,
      },
      layers: [
        { id: 'background', type: 'background', paint: { 'background-color': '#03060d' } },
        // Land silhouette under the photo, so the globe still reads if imagery is slow or blocked.
        {
          id: 'earth',
          type: 'fill',
          source: 'basemap',
          'source-layer': 'earth',
          paint: { 'fill-color': '#1f2a3d' },
        },
        {
          id: 'imagery',
          type: 'raster',
          source: 'imagery',
          paint: {
            'raster-saturation': -0.15,
            'raster-contrast': 0.05,
            'raster-fade-duration': 200,
          },
        },
        ...borderLayers(true),
      ],
    };
  }
  return {
    version: 8,
    projection: { type: 'globe' },
    glyphs: glyphsUrl,
    sources: { basemap },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': '#0a1a33' } },
      {
        id: 'earth',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'earth',
        paint: { 'fill-color': '#26324f' },
      },
      {
        id: 'landcover',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'landcover',
        paint: {
          'fill-color': [
            'match',
            ['get', 'kind'],
            'forest',
            '#233b4c',
            'glacier',
            '#3b4766',
            'farmland',
            '#2a3553',
            'grassland',
            '#283a51',
            'scrub',
            '#273651',
            'barren',
            '#2f3552',
            'urban_area',
            '#353b5c',
            '#26324f',
          ],
          'fill-opacity': 0.85,
        },
      },
      {
        id: 'water',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'water',
        paint: { 'fill-color': '#0a1a33' },
      },
      ...borderLayers(false),
    ],
  };
}
